import React, { useCallback, useMemo, useState } from "react";
import * as d3 from "d3";
import { useHistory } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { Card, CardFooter, CardBody } from "@ddm-design-system/card";
import { TabGroup, Tab } from "@ddm-design-system/tab";
import { Label, LabelMedium, SectionTitle } from "@ddm-design-system/typography";
import { LinkButton } from "@ddm-design-system/button";
import useContent from "../../hooks/useContent";
import styles from "./recent-sales.module.scss";
import Time from "../../lib/Time/Time";
import { ITimeRange } from "../../lib/Time/types";
import { setStartEndTimeRange } from "../../lib/Time/TimeFunctions";
import { setTimeFrame as setTimeFrameAction } from "../../store/filter/actions";
import Routes from "../../routes";
import RecentSalesCard from "./RecentSalesCard";
import RecentSalesInfo from "./RecentSalesInfo";
import "./recent-sales-info.scss";
import { getTimeFrame } from "../../store/filter/selectors";

const tabs: ITimeRange[] = ["YESTERDAY", "WEEK", "4WEEKS"];

const RecentSales: React.FC = () => {
  const storeTimeFrame = useSelector(getTimeFrame);
  const [timeFrame, setTimeFrame] = useState(storeTimeFrame);
  const timeRange = useMemo(() => timeFrame.type, [timeFrame]);
  const tabIndex = useMemo(
    () =>
      Math.max(
        tabs.findIndex(tab => tab === timeRange),
        0
      ),
    [timeRange]
  );
  const selectedTab = useMemo(() => tabs[tabIndex], [tabIndex]);
  const { managerAppOverview: content, units } = useContent();
  const dispatch = useDispatch();
  const dates = useMemo(() => setStartEndTimeRange(selectedTab), [selectedTab]);
  const startMoment = dates.start.toJSDate();
  const history = useHistory();

  const dateString = (
    <LabelMedium className={styles.dateString}>
      {timeFrame.type === "YESTERDAY"
        ? d3.timeFormat("%d %b")(startMoment)
        : `${d3.timeFormat("%d %b")(dates.start.toJSDate())} - ${d3.timeFormat("%d %b")(
            new Time().toJSDate()
          )}`}
    </LabelMedium>
  );

  const goToSales = useMemo(
    () => () => {
      dispatch(
        setTimeFrameAction({
          from: setStartEndTimeRange(selectedTab).start,
          to: setStartEndTimeRange(selectedTab).end,
          type: selectedTab
        })
      );
      history.push(Routes.sales);
    },
    [dispatch, history, selectedTab]
  );

  const handleTabClick = useCallback((index: React.Key) => {
    const newTab = tabs[index as number];
    setTimeFrame({
      from: setStartEndTimeRange(newTab).start,
      to: setStartEndTimeRange(newTab).end,
      type: newTab
    });
  }, []);

  const { timeStrings = {} } = units;
  const getTabTitle = useCallback(
    (timeframe: ITimeRange) =>
      timeframe === "YESTERDAY"
        ? timeStrings.unit_yesterday
        : timeframe === "WEEK"
        ? timeStrings.unit_last_7_days
        : timeStrings.unit_last_4_weeks,
    [timeStrings]
  );

  return (
    <div className={styles.container}>
      <div className={`${styles.titleWrapper} recent-sales-header`}>
        <SectionTitle className={styles.title}>
          {content.manager_app_recent_performance}
        </SectionTitle>
        <RecentSalesInfo />
      </div>
      <Card className={styles.recentSales}>
        <CardBody className={styles.card}>
          <div className={styles.header}>
            <TabGroup
              className={styles.recentSalesTabGroup}
              selectedTab={tabIndex}
              onTabClick={handleTabClick}
            >
              {tabs.map(tab => (
                <Tab key={tab} label={getTabTitle(tab)} className={styles.recentSalesTab} />
              ))}
            </TabGroup>
            <Label className={styles.dateText}>{dateString}</Label>
          </div>
          <div className={styles.content}>
            <RecentSalesCard id={`overview_${selectedTab.toLowerCase()}`} />
          </div>
        </CardBody>
        <CardFooter className={styles.cardFooter}>
          <LinkButton icon="chevron-right" onClick={goToSales}>
            {content[`manager_app_redirect_${selectedTab.toLowerCase()}_sales`]}
          </LinkButton>
        </CardFooter>
      </Card>
    </div>
  );
};

export default RecentSales;
